import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
import { useHelp } from '../../contexts/HelpContext'
import { LayoutDashboard, Users, Calendar, BarChart3, Settings, Trophy, Menu, X, ChevronLeft, ChevronRight, Monitor, Share2, Grid3x3, ArrowUpCircle, Plus, Sparkles, HelpCircle, LogOut, FileText, Smartphone, Crown, ShieldCheck, ChevronDown, Palette, Upload, Download, ExternalLink, Copy, QrCode, Handshake, ToggleLeft, ToggleRight, Terminal, Target } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'

import { useTranslation } from 'react-i18next'

export const Sidebar = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { tournamentId } = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { user, signOut } = useAuth()
  const { activeTenant } = useTenant()
  const { helpMode, toggleHelp } = useHelp()

  const filterParam = searchParams.get('filter')

  // State for menus
  const [tournamentOpen, setTournamentOpen] = useState(true)
  const [tournamentName, setTournamentName] = useState('')
  const [themeColor, setThemeColor] = useState('')
  const [sportType, setSportType] = useState('goals')
  const [adminMenuColored, setAdminMenuColored] = useState(false)

  useEffect(() => {
    if (tournamentId) {
      getTournamentData()
    } else {
      setTournamentName('')
      setThemeColor('')
      setSportType(filterParam || 'goals')
    }
  }, [tournamentId, filterParam])

  const getTournamentData = async () => {
    try {
      const { data, error } = await supabase.from('tournaments')
        .select('name, theme_color, admin_menu_colored, sport_type')
        .eq('id', tournamentId)
        .single()
      
      if (error || !data) {
        setTournamentName('')
        setThemeColor('')
        setAdminMenuColored(false)
        setSportType('goals')
        return
      }

      setTournamentName(data.name)
      setThemeColor(data.theme_color)
      setAdminMenuColored(data.admin_menu_colored)
      setSportType(data.sport_type || 'goals')
    } catch (err) {
      console.error('Error fetching tournament data in Sidebar:', err)
      setTournamentName('')
      setThemeColor('')
      setAdminMenuColored(false)
      setSportType('goals')
    }
  }


  const isEsportsActive = sportType === 'esports' || (location.pathname === '/admin/dashboard' && filterParam === 'esports')
  const isSetsActive = sportType === 'sets' || (location.pathname === '/admin/dashboard' && filterParam === 'sets')

  // Custom Render for split branding
  const renderBrand = () => {
    if (isEsportsActive) return (
      <div className="flex flex-col justify-center items-start leading-none pl-1">
        <span className="font-black text-lg tracking-tight uppercase italic text-white select-none">
          PRO<span className="text-cyan-400 drop-shadow-[0_0_6px_rgba(6,182,212,0.6)]">PULSE</span>
        </span>
        <span className="text-[7px] font-black tracking-[0.22em] uppercase text-white/40 -mt-0.5 font-sans">
          esports console
        </span>
      </div>
    );

    if (isSetsActive) return (
      <div className="flex flex-col justify-center items-start leading-none pl-1">
        <span className="font-black text-lg tracking-tight uppercase italic text-white select-none">
          PRO<span className="text-lime-400 drop-shadow-[0_0_6px_rgba(163,230,53,0.6)]">COURT</span>
        </span>
        <span className="text-[7px] font-black tracking-[0.22em] uppercase text-white/40 -mt-0.5 font-sans">
          racket console
        </span>
      </div>
    );

    if (!tournamentName) return (
      <span className="font-black text-2xl tracking-tighter uppercase italic shrink-0 pl-1">
        <span className="text-gray-800">PRO</span>
        <span className="text-yellow-500 ml-[0.15rem]">POULE</span>
      </span>
    );

    const parts = tournamentName.split(' ');
    const first = parts[0];
    const rest = parts.slice(1).join(' ');

    return (
      <span className="font-black text-2xl tracking-tighter uppercase italic shrink-0 pl-1" style={{ color: 'white' }}>
        {first}
        {rest && <span className="text-yellow-300 ml-[0.15rem]">{rest}</span>}
      </span>
    )
  }

  // ...


  const [settingsOpen, setSettingsOpen] = useState(false)
  const [dataOpen, setDataOpen] = useState(false)
  const [extrasOpen, setExtrasOpen] = useState(false) // Extra tools collapsed by default
  const [langOpen, setLangOpen] = useState(false) // Language dropdown

  // State for sidebar mode
  const [isCollapsed, setIsCollapsed] = useState(false)

  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [showQR, setShowQR] = useState(false)

  // Auto-collapse on small screens, expand on large
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(false) // On mobile, we use full drawer, not collapsed mode
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close mobile sidebar on navigation
  useEffect(() => {
    setIsMobileOpen(false)
  }, [location.pathname])

  const mainItems = [
    { label: t('common.dashboard'), path: '/admin/dashboard', icon: LayoutDashboard },
    { label: t('common.settings'), path: '/admin/profile', icon: Settings },
    { label: t('sidebar.help'), path: '/admin/help', icon: HelpCircle },
    { label: t('sidebar.sponsors'), path: '/admin/sponsors', icon: Handshake },
    { label: 'E2E Test Runner', path: '/admin/test-runner', icon: Terminal },
  ]

  const tournamentCoreItems = tournamentId ? [
    { label: t('sidebar.settings_tournament'), path: `/admin/tournaments/${tournamentId}/settings`, icon: Settings },
    { label: t('sidebar.divisions'), path: `/admin/tournaments/${tournamentId}/divisions`, icon: Grid3x3 },
    { label: t('sidebar.teams'), path: `/admin/tournaments/${tournamentId}/teams`, icon: Users },
    { label: t('sidebar.matches'), path: `/admin/tournaments/${tournamentId}/matches`, icon: Calendar },
    { label: t('sidebar.standings'), path: `/admin/tournaments/${tournamentId}/standings`, icon: BarChart3 },
    { label: t('sidebar.finals'), path: `/admin/tournaments/${tournamentId}/bracket`, icon: Trophy },
    { label: t('sidebar.presentation'), path: `/admin/tournaments/${tournamentId}/presentation`, icon: Monitor },
    { label: t('sidebar.sponsors') || 'Sponsoren & Ads', path: `/admin/tournaments/${tournamentId}/sponsors`, icon: Handshake },
    ...(isEsportsActive ? [
      { label: 'Esports Scout HQ', path: `/admin/tournaments/${tournamentId}/scouthq`, icon: Target }
    ] : [])
  ] : []

  const tournamentExtraItems = tournamentId ? [
    { label: t('sidebar.ladder'), path: `/admin/tournaments/${tournamentId}/ladder`, icon: ArrowUpCircle },
    { label: t('sidebar.king'), path: `/admin/tournaments/${tournamentId}/king`, icon: Crown },
    { label: t('sidebar.reports'), path: `/admin/tournaments/${tournamentId}/reports`, icon: FileText },
    { label: t('sidebar.access'), path: `/admin/tournaments/${tournamentId}/access`, icon: ShieldCheck },
  ] : []

  // Keep old tournamentItems as combined (for any other references)
  const tournamentItems = [...tournamentCoreItems, ...tournamentExtraItems]


  const publicLink = tournamentId ? `${window.location.protocol}//${window.location.host}/live/${tournamentId}` : ''

  const copyLink = () => {
    navigator.clipboard.writeText(publicLink)
    alert('Link gekopieerd!')
  }

  const sidebarWidth = isCollapsed ? 'w-20' : 'w-64'

  return (
    <>
      {/* Mobile Menu Button - Fixed to top left when sidebar is closed */}
      {!isMobileOpen && (
        <div className="md:hidden fixed top-4 left-4 z-50">
          <button
            onClick={() => setIsMobileOpen(true)}
            className="p-2 bg-white rounded-lg shadow-lg text-gray-700 hover:bg-gray-100"
          >
            <Menu size={24} />
          </button>
        </div>
      )}

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar Container */}
      <div className={`
        fixed md:relative z-40 h-[100dvh] transition-all duration-300 ease-in-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        ${sidebarWidth}
        flex flex-col glass-strong shadow-2xl group 
        ${isEsportsActive ? 'sidebar-esports text-slate-100' : isSetsActive ? 'sidebar-racketsports text-slate-100' : 'text-gray-800'}
        ${adminMenuColored && themeColor ? 'sidebar-themed' : ''}
      `} style={isEsportsActive ? { backgroundColor: 'rgba(8, 6, 16, 0.95)', borderRight: '1px solid rgba(168, 85, 247, 0.25)', boxShadow: '0 0 40px rgba(168, 85, 247, 0.15)' } : isSetsActive ? { backgroundColor: 'rgba(4, 8, 6, 0.95)', borderRight: '1px solid rgba(163, 230, 53, 0.25)', boxShadow: '0 0 40px rgba(163, 230, 53, 0.15)' } : { backgroundColor: (adminMenuColored && themeColor) ? themeColor : undefined, color: (adminMenuColored && themeColor) ? 'white' : undefined }}>
        {/* Toggle Collapse Button (Desktop only) */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden md:flex absolute -right-3 top-6 bg-slate-800 rounded-full p-1 shadow-md border border-slate-600 text-slate-400 hover:text-orange-500 transition-colors z-50"
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>



        {/* Header */}
        <div className={`p-4 flex items-center justify-between border-b border-white/20`}>
          <div
            onClick={() => navigate('/admin/dashboard')}
            className={`cursor-pointer flex items-center gap-2 transition-all min-w-0 ${isCollapsed ? 'mx-auto' : ''}`}
          >
            <img
              src="/logo-icon.webp"
              alt="ProPoule"
              className="w-10 h-10 object-contain drop-shadow-xl shrink-0 animate-pulse"
            />
            {!isCollapsed && renderBrand()}
          </div>
          
          {/* Mobile Close Button (inside sidebar) */}
          <button
            onClick={() => setIsMobileOpen(false)}
            className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>



        {/* Navigation */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 space-y-2">

          {/* Dashboard Item */}
          <NavItem
            icon={LayoutDashboard}
            label="Dashboard"
            collapsed={isCollapsed}
            active={location.pathname === '/admin/dashboard'}
            onClick={() => navigate('/admin/dashboard')}
          />

          {/* Special: WK POULE Entryway */}
          <div className="px-2 py-1">
            <button
              onClick={() => navigate('/wkpoule')}
              className={`
                w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-300
                bg-gradient-to-r from-orange-500 to-pink-600 text-white font-black shadow-md hover:shadow-lg transform hover:-translate-y-0.5
                ${isCollapsed ? 'justify-center' : 'px-4'}
              `}
              title="WK Poule 2026"
            >
              <Trophy size={20} fill="currentColor" className="shrink-0 animate-pulse" />
              {!isCollapsed && <span className="tracking-wide">WK POULE 2026</span>}
            </button>
          </div>

          {/* Tournament Section */}
          {tournamentId && (
            <div className="mt-4">
              {!isCollapsed && (
                <>
                  <div
                    className="px-4 py-2 flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => setTournamentOpen(!tournamentOpen)}
                  >
                    <span>{t('sidebar.tournament')}</span>
                    {tournamentOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </div>

                  {/* Public Link Button (Direct Open) */}
                  {tournamentId && tournamentOpen && !isCollapsed && (
                    <button
                      onClick={() => window.open(publicLink, '_blank')}
                      className="w-[calc(100%-1rem)] mx-2 flex items-center px-3 py-2 text-sm font-medium border border-gray-500/30 hover:bg-gray-500/10 transition-colors rounded-lg my-2 opacity-80 hover:opacity-100"
                    >
                      <ExternalLink size={16} className="mr-3 opacity-70" />
                      Bekijk Publieke Pagina
                    </button>
                  )}
                </>
              )}

              {(tournamentOpen || isCollapsed) && (
                <div className="space-y-1">
                  {/* Only show section icon in collapsed mode */}
                  {isCollapsed && (
                    <div className="w-full flex justify-center py-2 text-gray-400">
                      <Trophy size={20} />
                    </div>
                  )}

                  {/* Core flow items */}
                  {tournamentCoreItems.map((item, i) => (
                    <NavItem
                      key={i}
                      icon={item.icon}
                      label={item.label}
                      collapsed={isCollapsed}
                      active={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                      indent={!isCollapsed}
                    />
                  ))}

                  {/* Extra tools - collapsible group */}
                  {!isCollapsed && (
                    <button
                      onClick={() => setExtrasOpen(!extrasOpen)}
                      className="w-full flex items-center justify-between px-4 py-1.5 text-xs font-semibold text-gray-400 hover:text-gray-600 uppercase tracking-wider transition-colors mt-2"
                    >
                      <span>Extra Tools</span>
                      {extrasOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </button>
                  )}
                  {(extrasOpen || isCollapsed) && tournamentExtraItems.map((item, i) => (
                    <NavItem
                      key={`extra-${i}`}
                      icon={item.icon}
                      label={item.label}
                      collapsed={isCollapsed}
                      active={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                      indent={!isCollapsed}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* New Tournament (if none selected) */}
          {!tournamentId && (
            <NavItem
              icon={Plus}
              label={t('sidebar.new_tournament')}
              collapsed={isCollapsed}
              className="text-[var(--primary)] hover:bg-white/10"
              onClick={() => navigate('/admin/dashboard?new=true')}
            />
          )}

          {/* Tools & Settings Separator */}
          {!isCollapsed && <div className="px-4 py-2 mt-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('sidebar.management')}</div>}
          {isCollapsed && <div className="border-t border-gray-200 my-4 mx-4"></div>}



          {/* Settings Section */}

          {/* Design Link moved here (Only visible if tournament selected) */}
          {tournamentId && (
            <NavItem
              icon={Palette}
              label={t('sidebar.design')}
              collapsed={isCollapsed}
              active={location.pathname === `/admin/tournaments/${tournamentId}/design`}
              onClick={() => navigate(`/admin/tournaments/${tournamentId}/design`)}
            />
          )}

          <NavItem
            icon={Settings}
            label={t('sidebar.club_settings')}
            collapsed={isCollapsed}
            active={location.pathname === '/admin/settings'}
            onClick={() => navigate('/admin/settings')}
          />

          {activeTenant?.plan === 'pro' ? (
            <NavItem
              icon={Crown}
              label={t('sidebar.pro_membership')}
              collapsed={isCollapsed}
              active={location.pathname === '/admin/upgrade'}
              onClick={() => navigate('/admin/upgrade')}
              className="text-yellow-600 font-medium"
            />
          ) : (
            <NavItem
              icon={Crown}
              label={t('sidebar.upgrade')}
              collapsed={isCollapsed}
              active={location.pathname === '/admin/upgrade'}
              onClick={() => navigate('/admin/upgrade')}
              className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 font-bold animate-pulse"
            />
          )}

          <NavItem
            id="tour-sidebar-help" // ID FOR TOUR
            icon={HelpCircle}
            label={t('sidebar.help')}
            collapsed={isCollapsed}
            active={location.pathname === '/admin/help'}
            onClick={() => navigate('/admin/help')}
          />

          <NavItem
            icon={LogOut}
            label="Uitloggen"
            collapsed={isCollapsed}
            onClick={signOut}
            className="text-gray-500 hover:text-red-600 hover:bg-red-50 mt-4"
          />

          {/* Extracted Admin Tools Section */}
          {user && user.email === 'pascal.teunissen@gmail.com' && (
            <div className="mt-4 pt-4 border-t border-gray-200/20">
              {!isCollapsed && <div className="px-4 text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-wider">Lokaal Beheer</div>}

              {/* Test Runner Link */}
              <NavItem
                icon={Terminal}
                label="E2E Test Runner"
                collapsed={isCollapsed}
                active={location.pathname === '/admin/test-runner'}
                onClick={() => navigate('/admin/test-runner')}
                className="text-indigo-600 font-bold hover:bg-indigo-50"
              />
            </div>
          )}

          {/* Super Admin Link */}
          {user && user.email === 'pascal.teunissen@gmail.com' && (
            <div className="mt-4 pt-4 border-t border-gray-200/20">
              {!isCollapsed && <div className="px-4 text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-wider">{t('sidebar.super_admin')}</div>}
              <NavItem
                icon={LayoutDashboard}
                label={t('sidebar.platform_management')}
                collapsed={isCollapsed}
                active={location.pathname.startsWith('/platform')}
                onClick={() => navigate('/platform/dashboard')}
                className="text-orange-600 font-bold hover:bg-orange-50"
              />
            </div>
          )}

        </div>

        {/* Language Switcher — compact globe icon */}
        {!isCollapsed && (
          <div className="px-4 py-2 border-t border-white/10 mt-2 relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors px-2 py-1 rounded hover:bg-white/10"
              title="Taal kiezen"
            >
              <span className="text-base">🌐</span>
              <span className="font-bold uppercase">{i18n.language}</span>
            </button>
            {langOpen && (
              <div className="absolute bottom-10 left-4 bg-white shadow-lg rounded-xl border border-gray-100 py-1 z-50 min-w-[100px]">
                {['nl', 'en', 'es', 'de', 'fr'].map(lang => (
                  <button
                    key={lang}
                    onClick={() => { i18n.changeLanguage(lang); setLangOpen(false) }}
                    className={`w-full text-left px-4 py-2 text-sm uppercase font-bold hover:bg-gray-50 transition-colors ${i18n.language === lang ? 'text-[var(--accent-primary)]' : 'text-gray-600'
                      }`}
                  >
                    {lang === 'nl' ? '🇳🇱 NL' : lang === 'en' ? '🇬🇧 EN' : lang === 'es' ? '🇪🇸 ES' : lang === 'de' ? '🇩🇪 DE' : '🇫🇷 FR'}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Help Toggle Footer */}
        <div className="px-4 pb-2">
          {!isCollapsed ? (
            <button
              onClick={toggleHelp}
              className={`w-full flex items-center justify-between p-3 rounded-xl transition-all border ${helpMode ? 'bg-indigo-50 border-indigo-200 shadow-sm' : 'bg-transparent border-transparent hover:bg-white/10'}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-700 group-[.sidebar-themed]:text-white">🎓 {t('sidebar.help_mode')}</span>
              </div>
              {helpMode ? <ToggleRight className="text-indigo-600 group-[.sidebar-themed]:text-white" size={24} /> : <ToggleLeft className="text-gray-400 group-[.sidebar-themed]:text-white/60" size={24} />}
            </button>
          ) : (
            <button
              onClick={toggleHelp}
              className="w-full flex justify-center py-2"
              title={`${t('sidebar.help_mode')}: ${helpMode ? t('common.on') : t('common.off')}`}
            >
              {helpMode ? <ToggleRight className="text-indigo-600" size={24} /> : <ToggleLeft className="text-gray-400" size={24} />}
            </button>
          )}
        </div>

        {/* Footer / User Profile */}
        <div className="border-t border-white/20 p-4 bg-white/10 backdrop-blur-sm">
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
            <div className={`flex items-center gap-3 ${isCollapsed ? '' : 'flex-1 min-w-0'}`}>
              <div className="w-10 h-10 theme-gradient-bg rounded-full flex items-center justify-center text-sm font-bold text-white shadow-lg shrink-0">
                {user?.email?.[0]?.toUpperCase()}
              </div>

              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold truncate text-gray-900">{activeTenant?.name || user?.email}</p>
                    {activeTenant?.plan === 'pro' && (
                      <span className="bg-gradient-to-r from-yellow-300 to-yellow-500 text-yellow-900 text-[9px] font-black px-1.5 py-0.5 rounded uppercase shadow-sm flex items-center gap-1">
                        <Crown size={8} fill="currentColor" /> PRO
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">Admin</p>
                </div>
              )}
            </div>

            {!isCollapsed && (
              <button
                onClick={signOut}
                className="text-gray-500 hover:text-red-600 transition-colors p-2 hover:bg-red-50 rounded-lg"
                title="Uitloggen"
              >
                <LogOut size={18} />
              </button>
            )}
            {/* Show simple logout in collapsed */}
            {isCollapsed && (
              <div className="absolute left-16 bg-white shadow-md p-2 rounded-md hidden group-hover:block z-50">
                <button onClick={signOut}><LogOut size={16} /></button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center transform transition-all scale-100" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                <QrCode className="text-indigo-600" /> Publieke Link
              </h3>
              <button onClick={() => setShowQR(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="bg-white p-4 rounded-xl border-2 border-dashed border-gray-200 mb-4 flex justify-center">
              <QRCodeSVG value={publicLink} size={200} level="H" includeMargin={true} />
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Scan met je telefoon om de live standen en het programma te bekijken.
            </p>

            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={publicLink}
                className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button onClick={copyLink} className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors" title="Kopieer link">
                <Copy size={18} />
              </button>
              <button onClick={() => window.open(publicLink, '_blank')} className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors" title="Open in nieuw tabblad">
                <ExternalLink size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

import { soundFx } from '../../lib/soundEffects'

// Helper component for Nav Items
const NavItem = ({ icon: Icon, label, collapsed, active, onClick, indent = false, className = '', ...props }) => {

  const handleClick = (e) => {
    soundFx.play('navigate') // Iets zachter dan 'click' voor navigatie
    e.audioplayed = true
    if (onClick) onClick(e)
  }

  return (
    <button
      onClick={handleClick}
      title={collapsed ? label : ''}
      className={`
        w-full flex items-center transition-all duration-200
        ${collapsed ? 'justify-center px-2 py-4' : `px-4 py-3 ${indent ? 'pl-11' : ''}`}
        ${active
          ? 'active-nav-item bg-white/20 theme-text group-[.sidebar-themed]:text-white border-r-4 theme-border group-[.sidebar-themed]:border-white font-bold shadow-sm'
          : 'text-gray-700 group-[.sidebar-themed]:text-white/90 hover:bg-white/10 hover:text-[var(--primary)] group-[.sidebar-themed]:hover:text-white'
        }
        ${className}
      `}
      {...props}
    >
      <Icon size={20} className={active ? 'theme-text group-[.sidebar-themed]:text-white' : ''} />
      {!collapsed && <span className="ml-3 truncate">{label}</span>}
    </button>
  )
}
